import { apiFetch } from "../api";

// Returns the API base for push endpoints — bureau uses /notifications,
// seekers use /public/notifications. Both reach the same controller.
const pushBase = () =>
  typeof window !== "undefined" && localStorage.getItem("publicUserToken")
    ? "/public/notifications"
    : "/notifications";

// Web Push expects the VAPID public key as a Uint8Array (raw bytes), not
// base64-url. Convert it.
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) arr[i] = rawData.charCodeAt(i);
  return arr;
};

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const getPushPermission = () =>
  typeof Notification === "undefined" ? "denied" : Notification.permission;

// Ensures the service worker is registered. The SW file is already shipped
// at /sw.js as part of the existing PWA setup.
const ensureRegistration = async () => {
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) return existing;
  return navigator.serviceWorker.register("/sw.js");
};

// Returns the current PushSubscription if any, otherwise null. Does NOT
// prompt the user.
export const getExistingSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await ensureRegistration();
  return reg.pushManager.getSubscription();
};

// Prompts for permission (if not yet granted) and creates a server-side
// subscription. Returns the saved subscription object, or null if the user
// declined / push is disabled.
export const enablePush = async () => {
  if (!isPushSupported()) return null;

  // Fetch the server's VAPID public key first so we don't prompt the user
  // for permission only to fail later.
  const keyRes = await apiFetch(`${pushBase()}/push/public-key`);
  if (!keyRes?.enabled || !keyRes?.publicKey) {
    throw new Error("Push notifications are not configured on the server yet.");
  }

  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return null;
  } else if (Notification.permission !== "granted") {
    return null;
  }

  const reg = await ensureRegistration();

  // Some browsers retain a stale subscription tied to an old VAPID key —
  // detect mismatch and unsubscribe before re-subscribing.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await sub.unsubscribe();
    } catch (e) {
      /* non-fatal */
    }
  }

  sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(keyRes.publicKey),
  });

  const json = sub.toJSON();
  await apiFetch(`${pushBase()}/push/subscribe`, {
    method: "POST",
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      userAgent: navigator.userAgent,
    }),
  });

  return sub;
};

export const disablePush = async () => {
  if (!isPushSupported()) return;
  const reg = await ensureRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) {
    /* swallow */
  }
  try {
    await apiFetch(`${pushBase()}/push/unsubscribe`, {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    });
  } catch (e) {
    /* swallow */
  }
};
